import { createApiClient, readSessionCsrfToken } from '@aztec-realms/api-client';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';
export const BROWSER_SESSION_INVALIDATED_EVENT = 'aztec-realms:session-invalidated';
export const BROWSER_SESSION_ACTIVITY_EVENT = 'aztec-realms:session-activity';

const SESSION_INVALIDATION_CODES = new Set([
  'authentication_required',
  'session_expired',
  'csrf_missing',
  'csrf_invalid',
]);

type UnknownRecord = Record<string, unknown>;

export type BrowserApiClient = ReturnType<typeof createApiClient>;

export interface BrowserSessionInvalidatedDetail {
  code: string;
  message: string;
}

export class BrowserApiError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'BrowserApiError';
    this.code = code;
  }
}

export function invalidateBrowserSession(detail: BrowserSessionInvalidatedDetail): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<BrowserSessionInvalidatedDetail>(BROWSER_SESSION_INVALIDATED_EVENT, {
      detail,
    }),
  );
}

function notifySessionInvalidated(error: BrowserApiError): void {
  invalidateBrowserSession({ code: error.code, message: error.message });
}

function notifySessionActivity(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(BROWSER_SESSION_ACTIVITY_EVENT));
}

export function browserApiClient({
  mutation = false,
  csrfToken: suppliedCsrfToken,
}: { mutation?: boolean; csrfToken?: string } = {}) {
  const csrfToken = suppliedCsrfToken ?? (mutation ? readSessionCsrfToken() : undefined);
  if (mutation && !csrfToken) {
    const error = new BrowserApiError(
      'csrf_missing',
      'Your secure session is missing its CSRF token. Sign in again.',
    );
    notifySessionInvalidated(error);
    throw error;
  }
  return createApiClient({ baseUrl: API_BASE_URL, csrfToken });
}

export function apiError(error: unknown, fallback: string): BrowserApiError {
  let code = 'request_failed';
  let message = fallback;
  if (typeof error === 'object' && error !== null) {
    const outer = error as UnknownRecord;
    const payload = outer.error;
    if (typeof payload === 'object' && payload !== null) {
      const envelope = payload as UnknownRecord;
      if (typeof envelope.code === 'string' && envelope.code.length > 0) code = envelope.code;
      if (typeof envelope.message === 'string' && envelope.message.length > 0) {
        message = envelope.message;
      }
    }
    if (typeof outer.detail === 'string' && outer.detail.length > 0) message = outer.detail;
  }
  return new BrowserApiError(code, message);
}

export function isAuthenticationRequired(error: unknown): boolean {
  return error instanceof BrowserApiError && SESSION_INVALIDATION_CODES.has(error.code);
}

export function requiredApiData<T>(data: T | undefined, error: unknown, fallback: string): T {
  if (data === undefined) {
    const mappedError = apiError(error, fallback);
    if (isAuthenticationRequired(mappedError)) notifySessionInvalidated(mappedError);
    throw mappedError;
  }
  notifySessionActivity();
  return data;
}
